import { Field, FieldAttributes } from "formik";
import React, { useRef, useEffect } from "react";
import styled from "styled-components";
import { LoadingState } from "./states/LoadingState";
import { style } from "./style";
import { Container, DescriptionText, Icon, LabelText, LoadingWrapper, SvgIcon } from "./utils/CheckAndRadioUtils";
import { deprecationWarning } from "./utils/warning";

const tickSvg = `<svg width="12" height="9" viewBox="0 0 12 9" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M1 5L4 8L11 1" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
</svg>`;

const lineSvg = `<svg width="12" height="2" viewBox="0 0 12 2" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M1 1L11 1" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
</svg>
`;

type CheckboxInputProps = {
  $loading: boolean;
  $hasIcon: boolean;
};

const tick = encodeURIComponent(tickSvg);
const line = encodeURIComponent(lineSvg);

const warnInput = () => {
  deprecationWarning('"Checkbox"', '"CheckboxV2.Input"', "?path=/story/components-checkbox-input-v2--main");
};

const warnButton = () => {
  deprecationWarning('"Checkbox"', '"CheckboxV2.Button"', "?path=/story/components-checkbox-button-v2--main");
};

export const CheckboxInput = styled((props) => {
  warnInput();
  return <Field {...props} />;
})<CheckboxInputProps>`
  ${(props) => (props.$loading ? "display: none;" : "")};
  appearance: none;
  border-radius: 2px;
  width: ${style.vars.fontSize.base};
  height: ${style.vars.fontSize.base};
  grid-row: 1 / ${(props) => (props.$hasIcon ? "-1" : "1")};
  align-self: center;
  background-color: ${style.colors.white};
  box-sizing: border-box;
  pointer-events: none;

  border: ${style.vars.borderWidth.thin} solid ${({ isHovering }) => (isHovering ? style.colors.blue.dark : style.colors.grey.dark)};

  &:not(:disabled) {
    cursor: pointer;
  }

  &:disabled {
    background-color: ${style.colors.shadesOfGrey["1"]};
  }

  &:focus {
    outline: none;
  }

  &:checked {
    border-color: ${style.colors.blue.dark};
    background: url("data:image/svg+xml;charset=utf-8,${tick}") ${style.colors.blue.dark} no-repeat center center;
  }

  &:indeterminate {
    border-color: ${style.colors.blue.dark};
    background: url("data:image/svg+xml;charset=utf-8,${line}") ${style.colors.blue.dark} no-repeat center center;
  }
`;

type Props = {
  value: string;
  name: string;
  label: string;
  checked: boolean;
  description?: React.ReactNode;
  required?: boolean;
  disabled?: boolean;
  loading?: boolean;
  className?: string;
  icon?: string;
  iconSrc?: string;
  iconAltText?: string;
};

/**
 * @deprecated Use `CheckboxV2` instead
 */
export const Checkbox: React.FC<Props | FieldAttributes<any>> = ({
  value,
  name,
  label,
  description,
  checked,
  required,
  disabled,
  loading,
  className,
  icon,
  iconAltText = "",
  indeterminate,
  iconSrc,
  ...inputProps
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const id = `${name}-${value}`;

  const isLoading = loading && checked && !disabled;
  const isBoxMode = !!description || !!icon;
  const hasIcon = !!iconSrc || !!icon;

  warnButton();

  useEffect(() => {
    const checkBoxElement = inputRef.current;

    if (checkBoxElement) {
      checkBoxElement.indeterminate = indeterminate;
    }
  }, [indeterminate, inputRef.current]);

  return (
    <Container
      htmlFor={id}
      className={className}
      checked={checked}
      disabled={disabled}
      isBoxMode={isBoxMode}
      hasIcon={!!hasIcon}
      hasDescription={!!description}
    >
      <CheckboxInput
        id={id}
        type="checkbox"
        checked={checked}
        name={name}
        required={required}
        value={value}
        disabled={disabled}
        innerRef={inputRef}
        $loading={isLoading}
        $hasIcon={!!hasIcon}
        aria-label={label}
        {...inputProps}
      />
      {isLoading && (
        <LoadingWrapper hasIcon={hasIcon}>
          <LoadingState />
        </LoadingWrapper>
      )}
      {hasIcon && (iconSrc ? <Icon src={iconSrc} alt={iconAltText} /> : <SvgIcon icon={icon} />)}
      <LabelText checked={checked} hasDescription={!!description} isBoxMode={isBoxMode}>
        {label}
      </LabelText>
      {!!description && <DescriptionText $hasIcon={hasIcon}>{description}</DescriptionText>}
    </Container>
  );
};
